import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { FiArrowDown } from 'react-icons/fi';
import { career } from '../data/career';
import { education } from '../data/education';
import { bio, details } from '../data/about';
import { renderLinks } from '../utils/renderLinks';

const ArrowDown = () => <span style={{ display: 'flex' }}>{FiArrowDown({ size: 14 })}</span>;

/* ─── animation helpers ─── */
const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.12 } },
};

/* ─── styled components ─── */

const Page = styled(motion.div)`
  min-height: 100vh;
`;

/* ── Hero ── */

const Hero = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8rem 2.5rem 4rem;
  max-width: 1100px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 7rem 1.25rem 3rem;
  }
`;

const HeroInner = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 3rem;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const NameBlock = styled.div``;

const Name = styled(motion.h1)`
  font-family: var(--font-display);
  font-weight: 300;
  font-size: clamp(3.5rem, 8vw, 7rem);
  line-height: 0.92;
  letter-spacing: -0.02em;
  color: var(--text);
`;

const AccentLine = styled(motion.div)`
  width: 48px;
  height: 2.5px;
  background: var(--accent);
  margin: 1.5rem 0;
`;

const RoleText = styled(motion.p)`
  font-family: var(--font-mono);
  font-size: 0.8rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  line-height: 1.8;
`;

const HeroSide = styled(motion.div)`
  text-align: right;

  @media (max-width: 900px) {
    text-align: left;
  }
`;

const YearLabel = styled.span`
  font-family: var(--font-display);
  font-size: clamp(5rem, 10vw, 9rem);
  font-weight: 300;
  color: var(--border);
  line-height: 1;
  user-select: none;
`;

const IntroText = styled(motion.p)`
  font-family: var(--font-body);
  font-size: 1.05rem;
  line-height: 1.75;
  color: var(--text-muted);
  max-width: 540px;
  margin-top: 2rem;
`;

const ScrollHint = styled(motion.div)`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-family: var(--font-mono);
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

/* ── Section shared ── */

const Section = styled.section`
  padding: 6rem 2.5rem;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 4rem 1.25rem;
  }
`;

const SectionHeader = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 3.5rem;
`;

const SectionNumber = styled.span`
  font-family: var(--font-display);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--accent);
`;

const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.75rem;
  font-weight: 400;
  letter-spacing: 0.02em;
`;

const SectionRule = styled.div`
  flex: 1;
  height: 1px;
  background: var(--border);
`;

/* ── Career timeline ── */

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineEntry = styled(motion.div)`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 2rem;
  padding: 2rem 0;
  border-top: 1px solid var(--border);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 0;
    height: 1px;
    background: var(--accent);
    transition: width 0.5s ease;
  }

  &:hover::before {
    width: 160px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const TimelineDates = styled.div`
  font-family: var(--font-mono);
  font-size: 0.72rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
  padding-top: 0.2rem;
`;

const PresentHighlight = styled.span`
  color: var(--accent);
  border: 1px solid var(--accent);
  padding: 0.1rem 0.4rem;
  border-radius: 2px;
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TimelineContent = styled.div``;

const TimelineRole = styled.h3`
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
  line-height: 1.3;
`;

const TimelineOrg = styled.span`
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--text-muted);
  display: block;
  margin-bottom: 0.65rem;
`;

const TimelineDesc = styled.p`
  font-size: 0.88rem;
  color: var(--text-muted);
  line-height: 1.65;
  max-width: 620px;
`;

const BulletList = styled.ul`
  list-style: disc;
  padding-left: 1.15rem;
  margin: 0;

  li {
    font-size: 0.88rem;
    color: var(--text-muted);
    line-height: 1.65;
    max-width: 620px;
    margin-bottom: 0.3rem;

    &:last-child {
      margin-bottom: 0;
    }

    a {
      color: var(--accent);
    }
  }
`;

const TimelineTag = styled.span`
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
  border: 1px solid var(--accent);
  padding: 0.15rem 0.5rem;
  border-radius: 2px;
  margin-top: 0.75rem;
`;

/* ── About snippet ── */

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutText = styled(motion.p)`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text);
`;

const DetailBlock = styled(motion.div)``;

const DetailLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const DetailValue = styled.div`
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 400;
  line-height: 1.5;
`;

const DetailsColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;

/* ─── component ─── */

const Home: React.FC = () => (
  <Page
    initial="initial"
    animate="animate"
    exit="exit"
    variants={fade}
    transition={{ duration: 0.5 }}
  >
    {/* ── Hero ── */}
    <Hero>
      <HeroInner variants={stagger} initial="initial" animate="animate">
        <NameBlock>
          <Name variants={fade}>
            Aaron
            <br />
            Leslie
          </Name>
          <AccentLine
            variants={fade}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 48, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          />
          <RoleText variants={fade}>
            Software Engineer &amp; Co-founder
            <br />
            Building Glyph — an AI startup
          </RoleText>
          <IntroText variants={fade}>
            Previously SDE II at Amazon. Indiana University '22, BS Computer
            Science with a minor in East Asian Language &amp; Culture. Now
            building something new.
          </IntroText>
        </NameBlock>
        <HeroSide variants={fade}>
          <YearLabel aria-hidden="true">'26</YearLabel>
        </HeroSide>
      </HeroInner>

      <ScrollHint
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
      >
        <span>Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
        >
          <ArrowDown />
        </motion.div>
      </ScrollHint>
    </Hero>

    {/* ── Career ── */}
    <Section>
      <SectionHeader
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
      >
        <SectionNumber>01</SectionNumber>
        <SectionTitle>Career</SectionTitle>
        <SectionRule />
      </SectionHeader>

      <TimelineList>
        {career.map((item, i) => (
          <TimelineEntry
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
          >
            <TimelineDates>
              {item.dates.includes('Present')
                ? <>{item.dates.replace('Present', '')}<PresentHighlight>Present</PresentHighlight></>
                : item.dates}
            </TimelineDates>
            <TimelineContent>
              <TimelineRole>{item.role}</TimelineRole>
              <TimelineOrg>{item.org}</TimelineOrg>
              {item.desc && <TimelineDesc>{renderLinks(item.desc)}</TimelineDesc>}
              {item.bullets && (
                <BulletList>
                  {item.bullets.map((b, j) => (
                    <li key={j}>{renderLinks(b)}</li>
                  ))}
                </BulletList>
              )}
              {item.tag && <TimelineTag>{item.tag}</TimelineTag>}
            </TimelineContent>
          </TimelineEntry>
        ))}
      </TimelineList>
    </Section>

    {/* ── Education ── */}
    <Section>
      <SectionHeader
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
      >
        <SectionNumber>02</SectionNumber>
        <SectionTitle>Education</SectionTitle>
        <SectionRule />
      </SectionHeader>

      <TimelineList>
        {education.map((item, i) => (
          <TimelineEntry
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            <TimelineDates>{item.dates}</TimelineDates>
            <TimelineContent>
              <TimelineRole>{item.degree}</TimelineRole>
              <TimelineOrg>{item.school}</TimelineOrg>
              {item.desc && <TimelineDesc>{renderLinks(item.desc)}</TimelineDesc>}
              {item.bullets && (
                <BulletList>
                  {item.bullets.map((b: string, j: number) => (
                    <li key={j}>{renderLinks(b)}</li>
                  ))}
                </BulletList>
              )}
            </TimelineContent>
          </TimelineEntry>
        ))}
      </TimelineList>
    </Section>

    {/* ── About ── */}
    <Section>
      <SectionHeader
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
      >
        <SectionNumber>03</SectionNumber>
        <SectionTitle>About</SectionTitle>
        <SectionRule />
      </SectionHeader>

      <AboutGrid>
        <AboutText
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          {renderLinks(bio)}
        </AboutText>

        <DetailsColumn
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          {details.map((d, i) => (
            <DetailBlock key={i} variants={fade}>
              <DetailLabel>{d.label}</DetailLabel>
              <DetailValue>{d.value}</DetailValue>
            </DetailBlock>
          ))}
        </DetailsColumn>
      </AboutGrid>
    </Section>
  </Page>
);

export default Home;
